/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * May this user use the AI Assistant right now?
 *
 * Every AI entry point (chat button, "Create with AI", "Score with AI") gates
 * on `canUseAiAssistant`: the tenant bought the AI_ASSISTANT add-on, the
 * wallet has credits, and this user holds a seat. The answer comes from
 * GET /ai-assistant/status, fetched once per session and shared by every
 * caller, and refetched when the access token changes (another tenant or
 * user) or when `refresh()` is called (e.g. after an "out of credits" reply).
 *
 * Nothing here knows, or can show, which provider or model serves the tenant.
 */

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "auth/Auth";
import { useModuleEntitlements } from "hooks/useModuleEntitlements";

const EMPTY = { licensed: false, has_credits: false, has_access: false };

// Shared across hook instances for the session.
let cached = null;
let inflight = null;
const listeners = new Set();

function publish(value) {
  cached = value;
  listeners.forEach((fn) => fn(value));
}

if (typeof window !== "undefined") {
  window.addEventListener("auth-token-updated", () => {
    cached = null;
    inflight = null;
    publish(null);
  });
}

export function resetAiAssistantStatusCache() {
  cached = null;
  inflight = null;
}

export default function useAiAssistantStatus() {
  const { axiosInstance, isAuthReady, isAuthenticated } = useAuth();
  const { isModuleEnabled, loading: entitlementsLoading } = useModuleEntitlements();
  const [status, setStatus] = useState(cached);

  useEffect(() => {
    listeners.add(setStatus);
    return () => listeners.delete(setStatus);
  }, []);

  const load = useCallback(
    async (force = false) => {
      if (!isAuthReady || !isAuthenticated || !axiosInstance) return;
      if (!force && cached) {
        setStatus(cached);
        return;
      }
      if (!force && inflight) {
        await inflight;
        return;
      }
      inflight = axiosInstance
        .get("/ai-assistant/status")
        .then((res) => publish({ ...EMPTY, ...(res.data || {}) }))
        // A failed check hides AI features; it never shows them.
        .catch(() => publish({ ...EMPTY }))
        .finally(() => {
          inflight = null;
        });
      await inflight;
    },
    [axiosInstance, isAuthReady, isAuthenticated]
  );

  useEffect(() => {
    if (status === null) load();
  }, [status, load]);

  const licensedByEntitlement = !entitlementsLoading && isModuleEnabled("AI_ASSISTANT");
  const current = status || EMPTY;

  return {
    status: current,
    loading: status === null,
    canUseAiAssistant: Boolean(licensedByEntitlement && current.has_credits && current.has_access),
    refresh: () => load(true),
  };
}
